import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Careers = () => {
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 overflow-hidden">
      {/* Background atmosphere */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-100/40 via-transparent to-transparent" />
        <div className="absolute top-0 right-0 w-[80vmax] h-[80vmax] bg-amber-200/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[60vmax] h-[60vmax] bg-amber-100/40 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(251,191,36,0.12),transparent)]" />
      </div>

      {/* Header */}
      <header className="relative z-50 border-b border-stone-200/80 bg-white/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-3 text-stone-700 hover:text-stone-900 transition-colors"
            >
              <img src="/logo-bright.svg" alt="RestroAI" className="h-9 w-auto" />
              <span className="text-sm font-medium text-stone-500">Careers</span>
            </Link>
            <nav className="flex items-center gap-4">
              <Link to="/" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Home</Link>
              <Link to="/restaurants" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Restaurants</Link>
              <Link
                to="/login"
                className="text-sm font-semibold text-amber-600 hover:text-amber-700 px-5 py-2.5 rounded-full bg-amber-50 border border-amber-200 hover:border-amber-300 transition-all duration-300"
              >
                Login
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <motion.header
          className="mb-12 text-center"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-amber-600 text-sm font-semibold tracking-widest uppercase mb-2">Join us</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-2">Careers at RestroAI</h1>
          <p className="text-stone-600">Join us in building the future of restaurant management</p>
        </motion.header>

        <div className="space-y-8">
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-200/80 shadow-sm"
          >
            <div className="text-center">
              <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-50 border border-amber-100 text-3xl mb-4" aria-hidden="true">
                📋
              </span>
              <h2 className="text-xl font-semibold text-stone-900 mb-2">Current Openings</h2>
              <p className="text-stone-600 leading-relaxed">
                We don’t have any open positions at the moment. We’re a small team and we hire when the right opportunity comes up.
              </p>
              <p className="text-stone-500 mt-4">
                Check back here for updates, or get in touch—we’d love to hear from you if you’re interested in working with us in the future.
              </p>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-200/80 shadow-sm"
          >
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Why RestroAI?</h2>
            <p className="text-stone-600 mb-4">
              RestroAI is an AI-powered restaurant management platform used by hundreds of restaurants. We work on real-time systems, analytics, and products that help restaurant owners run their businesses better.
            </p>
            <ul className="space-y-3 text-stone-600 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 font-medium">✓</span>
                <span>Work on products that impact real businesses every day</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 font-medium">✓</span>
                <span>Collaborative, small-team environment</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 font-medium">✓</span>
                <span>Modern tech stack and room to grow</span>
              </li>
            </ul>
          </motion.section>

          <motion.section
            className="text-center"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <p className="text-stone-600 mb-6">
              Interested in future opportunities? Reach out and we’ll keep you in mind.
            </p>
            <Link
              to="/contact-us"
              className="inline-block bg-amber-500 hover:bg-amber-600 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/30"
            >
              Contact Us
            </Link>
          </motion.section>
        </div>

        <footer className="mt-16 pt-8 border-t border-stone-200 text-stone-500 text-sm text-center">
          <p>© {new Date().getFullYear()} RestroAI. All rights reserved.</p>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link to="/" className="hover:text-amber-600 transition-colors">Home</Link>
            <Link to="/contact-us" className="hover:text-amber-600 transition-colors">Contact</Link>
            <Link to="/blog" className="hover:text-amber-600 transition-colors">Blog</Link>
          </p>
        </footer>
      </main>
    </div>
  );
};

export default Careers;
